import { createSelector } from 'reselect'
import { adaptSitesV4ToLegacySites } from 'lib/api/adapters/sites'
import { isV4Routed } from 'lib/api/ssapi/secondStepApi.router'
import {
  extractProgramCodesFromCapabilities,
  normalizeClaims,
  getGradesForProgramFromUserAccess,
} from './helpers'
import { getProgramKeyFromProgramCode } from 'experienceComponents/CreateInstanceShared/dataUtils'
import {
  elementaryV2ContentfulMappings,
  middleSchoolV2ContentfulMappings,
} from 'utils/productMaps'

const selectSites = state => {
  const useV4 = isV4Routed('getUserSites')
  if (useV4 && Array.isArray(state.ssapiManager?.sites)) {
    // Store raw v4, adapt on read
    return adaptSitesV4ToLegacySites(state.ssapiManager.sites)
  }
  return state.licenseManager.sites
}
const getSiteId = (_, siteId) => siteId

export const selectSiteById = createSelector(
  [selectSites, getSiteId],
  (sites, siteId) => {
    let site
    if (siteId && sites) {
      site = sites?.find(site => site.id === siteId)
    }
    return site
  },
)

/**
 * Gets curriculum program codes (excludes training licenses like BPU/CPU)
 * @param {Object} state - Redux state
 * @param {number} siteId - Site ID
 * @returns {string[]} - Curriculum program codes only
 */
export const selectCurriculumProgramCodes = createSelector(
  [selectSiteById],
  site => {
    if (!site) return []
    const programCodes = Array.isArray(site.programCodes)
      ? site.programCodes
      : []
    const curriculumProgramCodes = [
      'K5P',
      'MSP',
      'HSP',
      'K8P',
      'K12P',
      'ADL',
      'SELA',
      'K5S',
    ]
    return programCodes.filter(code => curriculumProgramCodes.includes(code))
  },
)

/**
 * Base selector for sites filtered by programCode and claims
 * Used by both classes and small groups selectors
 * @private
 */
const selectFilteredSitesForProgram = createSelector(
  [
    state => state.ssapiManager?.sites, // Raw v4 sites (no adapter)
    (_, programCode) => programCode, // 'K5P' or 'MSP'
  ],
  (v4Sites, programCode) => {
    if (!['K5P', 'MSP'].includes(programCode)) return []
    if (!Array.isArray(v4Sites) || v4Sites.length === 0) return []

    return v4Sites
      .filter(site => {
        if (!site || !site.id) return false
        const siteProgramCodes = extractProgramCodesFromCapabilities(
          site.capabilities,
        )
        if (!siteProgramCodes.includes(programCode)) return false
        const claims = normalizeClaims(site.claims)
        return claims.includes('Teacher') || claims.includes('Admin')
      })
      .map(site => {
        const siteProgramCodes = extractProgramCodesFromCapabilities(
          site.capabilities,
        )
        return {
          siteId: site.id,
          siteName: site.name,
          programCodes: siteProgramCodes,
          claims: normalizeClaims(site.claims),
          id: site.id.toString(),
        }
      })
      .sort((a, b) => a.siteName.localeCompare(b.siteName))
  },
)

/**
 * Selector for CreateCourse: filters sites and adds grades from userAccess
 * Returns sites with individual grades (K, 1, 2, etc.) for class creation
 *
 * @param {Object} state - Redux state
 * @param {string} programCode - Program code ('K5P' or 'MSP')
 * @returns {Array} - Sites with grades: [{ siteId, siteName, programCodes, claims, id, grades: ['K', '1', '2', ...] }]
 */
export const selectSitesWithGradesForClasses = createSelector(
  [
    selectFilteredSitesForProgram,
    state => state.userAccessManager?.access,
    (_, programCode) => programCode,
  ],
  (filteredSites, userAccess, programCode) => {
    if (filteredSites.length === 0) return []

    const programKey = getProgramKeyFromProgramCode(programCode)
    if (!programKey) return []

    // Get grades from userAccess (site-agnostic)
    const grades = getGradesForProgramFromUserAccess(userAccess, programKey)
    if (grades.length === 0) return []

    // Add same grades to all sites
    return filteredSites.map(site => ({
      ...site,
      grades,
      licenseId: null, // Keep for backward compatibility
    }))
  },
)

/**
 * Selector for CreateSmallGroup: filters sites and adds tier2 curriculum grades from mappings
 * Returns sites with tier2 curriculum course names (k-2_curriculum_tier2, etc.) for small group creation
 *
 * @param {Object} state - Redux state
 * @param {string} programCode - Program code ('K5P' or 'MSP')
 * @returns {Array} - Sites with grades: [{ siteId, siteName, programCodes, claims, id, grades: ['k-2_curriculum_tier2', '3-5_curriculum_tier2', ...] }]
 */
export const selectSitesWithGradesForSmallGroups = createSelector(
  [selectFilteredSitesForProgram, (_, programCode) => programCode],
  (filteredSites, programCode) => {
    if (filteredSites.length === 0) return []

    // Get tier2 curriculum course names from mappings
    const mappings =
      programCode === 'K5P'
        ? elementaryV2ContentfulMappings
        : middleSchoolV2ContentfulMappings

    const grades = mappings
      .filter(mapping => mapping.courseName?.includes('_curriculum_tier2'))
      .map(mapping => mapping.courseName)
      .filter(Boolean)

    if (grades.length === 0) return []

    // Add same grades to all sites
    return filteredSites.map(site => ({
      ...site,
      grades,
      licenseId: null, // Keep for backward compatibility
    }))
  },
)

export default {
  selectSiteById,
  selectCurriculumProgramCodes,
  selectSitesWithGradesForClasses,
  selectSitesWithGradesForSmallGroups,
}
